import * as Sentry from '@sentry/vue';
import { isExpected404 } from '#shared/utils/is-expected-404';

/**
 * Strip the `code` query param from any URL whose pathname is /auth/callback.
 * Used to scrub Sentry breadcrumbs and event request URLs so the one-time SSO
 * exchange code never reaches Sentry. All other URLs pass through untouched.
 */
function stripCodeFromUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  try {
    const u = new URL(url, 'http://localhost'); // base for relative URLs
    if (u.pathname !== '/auth/callback') return url;
    if (!u.searchParams.has('code')) return url;
    u.searchParams.delete('code');
    // Reconstruct preserving original form (relative vs absolute)
    return url.startsWith('http')
      ? u.toString()
      : u.pathname + (u.search || '');
  } catch {
    return url;
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig();
  const dsn = config.public.sentryDsn;

  if (!dsn) return;

  Sentry.init({
    app: nuxtApp.vueApp,
    dsn,
    environment: config.public.sentryEnvironment || 'development',
    // Build SHA inlined into the client bundle via `vite.define` (see
    // nuxt.config.ts). `runtimeConfig.public.appVersion` is empty in the
    // browser — serialized from the Lambda runtime env, where
    // `NUXT_PUBLIC_APP_VERSION` is absent — so it can't be relied on here.
    // Stays `undefined` when unset (local dev), matching the server plugin, so
    // Sentry never tags a meaningless 'dev' release; a real deploy tags the SHA.
    release: __APP_VERSION__ || undefined,
    integrations: [
      Sentry.browserTracingIntegration({
        router: useRouter(),
      }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.5,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) {
        const data = breadcrumb.data as Record<string, unknown>;
        if (typeof data.from === 'string') {
          data.from = stripCodeFromUrl(data.from);
        }
        if (typeof data.to === 'string') {
          data.to = stripCodeFromUrl(data.to);
        }
        if (typeof data.url === 'string') {
          data.url = stripCodeFromUrl(data.url);
        }
      }
      return breadcrumb;
    },
    beforeSend(event, hint) {
      // Expected 404s (route validate misses, not-found fetches) are user
      // behavior, not server faults — drop them so they don't create events.
      if (isExpected404(hint?.originalException)) return null;
      if (event.request?.url) {
        event.request.url = stripCodeFromUrl(event.request.url);
      }
      return event;
    },
  });
});
